﻿import { capture } from '@ErrorHandling/ErrorLogging';
import { calculateDuration } from '@SharedScripts/Utils/Time';

export type ApiRequestProps = {
  url: string,
  method?: 'GET' | 'POST' | 'DELETE' | 'PUT',
  headers?: HeadersInit,
  body?: Record<string, unknown> | Array<unknown>,
  timeout?: number;
  origin?: string | false;
  withCredentials?: boolean;
}

type TimeoutError = {
  message: string;
  status: -1;
  ok: false;
}

let isBeforeUnloadFired = false;
let isUnloadFired = false;

const ApiRequest = async <T>({
  url,
  method = 'GET',
  headers = {},
  body,
  timeout = 10,
  origin = false,
  withCredentials = false
}: ApiRequestProps): Promise<T> => {
  const startTime = new Date();

  const defaultHeaders: HeadersInit = {
    Accept: 'application/json'
  };

  const options: RequestInit = {
    method,
    headers: {
      ...defaultHeaders,
      ...headers
    }
  };

  if (withCredentials) {
    options.credentials = 'include';
  }

  if (body) {
    options.body = JSON.stringify(body);

    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json'
    };
  }

  const response: Response | TimeoutError = await Promise.race([fetch(url, options as RequestInit), timeoutPromise(timeout, url)]);

  try {
    if (!response.ok) throw new Error(`Network response failed with status code: ${response.status}`);
    const responseData = await response.text();
    return responseData ? JSON.parse(responseData) : {};
  } catch (error) {
    const server = 'headers' in response && response.headers.has('X-DS-Env') ? response.headers.get('X-DS-Env') : '';
    const message = 'ApiRequest: ' + method + ' ' + filteredUrl(url) + (response.status ? ' [' + response.status + ']' : '') + (error ? ' (' + error + ')' : '');

    capture(message, {
      extra: {
        data: body,
        duration: calculateDuration(startTime),
        error,
        response: 'json' in response ? await response.json() : '',
        server: server,
        status: response.status,
        type: method,
        origin: origin,
        url,
        isBeforeUnloadFired: isBeforeUnloadFired,
        isUnloadFired: isUnloadFired
      },
      level: 'warning',
      tags: { custom: 'ApiRequest.ts' }
    });

    throw error;
  }
};

const timeoutPromise = (timeout: number, url: string): Promise<TimeoutError> => new Promise((_ignoreResolve, reject) => {
  setTimeout(reject, timeout * 1000, {
    message: `Frontend timed out after ${timeout} seconds: ${url}`,
    status: -1,
    ok: false
  } satisfies TimeoutError);
});

export default ApiRequest;

const filteredUrl = (url: string) => url.split('?')[0].split('/').map(function (segment, index) {
  if (segment === null || segment === '') return segment;

  const lowerSegment = segment.toLowerCase();

  if (lowerSegment === 'dli' || lowerSegment === 'dlo') return '<DLIO>';
  if (!isNaN(Number(segment))) return '<NO>';
  if (/^[0-9a-fA-F]{32}$/.test(segment)) return '<UUID32bytes>';
  if (/^[0-9a-fA-F]{16}$/.test(segment)) return '<UUID16bytes>';

  // Making the last parts generic/merged into same Sentry events, eg:
  // /<DLIO>/scapi/danskespil/playeraccountmanagement/playergames/<NO>/<NO>/<NO>/alle-spil/alle-kanaler
  // ---> /<DLIO>/scapi/danskespil/playeraccountmanagement/playergames/<NO>/<NO>/<NO>/<CHUNK>/<CHUNK>
  if (index >= 7) return '<CHUNK>';

  return segment;
}).join('/');

window.addEventListener('beforeunload', function () {
  console.debug('ApiRequest - beforeunload event fired');
  isBeforeUnloadFired = true;
});
window.addEventListener('unload', function () {
  console.debug('ApiRequest - unload event fired');
  isUnloadFired = true;
});
